import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { AccordionItem } from "@heroui/react";
import { Check, Gift } from "lucide-react";
import type { ComponentProps } from "react";
import { LimeAccordion } from "@/Components/NextBase/LimeAccordion";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import type { PostCheckPromoCode } from "@/server-types";

const PROMO_KEY = "promo";

export function OnboardingPromoCode({
  inputProps,
  isChecking,
  result,
}: {
  inputProps: ComponentProps<typeof LimeInput>;
  isChecking: boolean;
  result: PostCheckPromoCode["response"] | undefined;
}) {
  const { t } = useLingui();
  return (
    <LimeAccordion
      data-identifier="promo-code-section"
      aria-label={t`Promocijska koda`}
      className="px-0"
      // A code that was already typed keeps the field open when the step is
      // revisited, so it can be seen and changed.
      defaultExpandedKeys={inputProps.value ? [PROMO_KEY] : []}
      itemClasses={{
        base: "rounded-xl border border-primary/30 bg-primary/10 px-3",
        trigger: "h-11 gap-3 py-0",
        title: "font-medium text-sm",
        content: "flex flex-col gap-3 pt-0 pb-3",
      }}
    >
      <AccordionItem
        key={PROMO_KEY}
        data-identifier="promo-code-toggle"
        aria-label={t`Imate priporočilno ali promocijsko kodo?`}
        startContent={<Gift size={18} className="shrink-0 text-primary" />}
        title={<Trans>Imate priporočilno ali promocijsko kodo?</Trans>}
      >
        <LimeInput
          label={t`Koda`}
          data-identifier="promo-code-input"
          autoComplete="off"
          isLoading={isChecking}
          {...inputProps}
          error={
            result?.valid === false
              ? t`Promocijska koda ni veljavna ali pa je potekla`
              : undefined
          }
        />

        {result?.valid === true && (
          <div className="flex items-center gap-2 font-medium text-primary text-sm">
            <Check size={18} className="shrink-0" />
            <p>
              <Trans>
                Koda je veljavna: {result.promoCode.discountPercent} % popusta
                za{" "}
                <Plural
                  value={result.promoCode.discountMonths}
                  one="# mesec"
                  two="# meseca"
                  few="# mesece"
                  other="# mesecev"
                />
              </Trans>
            </p>
          </div>
        )}
      </AccordionItem>
    </LimeAccordion>
  );
}
